import type { AnalyticsProvider, GtmEventMap } from '~analytics/types/analyticsProvider';

interface GoogleTagManager {
  proxy: {
    dataLayer: {
      push: (data: object) => void;
    };
  };
}

// GA4 rejects null param values and evaluatedFlags is a large JSON blob with no GA use
const GTM_EXCLUDED_PARAMS = ['evaluatedFlags'];

const toFlatParams = (eventData: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(eventData).filter(
      ([key, value]) => value !== null && value !== undefined && !GTM_EXCLUDED_PARAMS.includes(key),
    ),
  );

export default class GoogleTagManagerProvider implements AnalyticsProvider {
  serviceName: string;
  subscribedEvents: string[];
  googleTagManager?: GoogleTagManager;
  mappedEvents: GtmEventMap;

  constructor(
    serviceName: string,
    mappedEvents: GtmEventMap,
  ) {
    this.serviceName = serviceName;
    this.mappedEvents = mappedEvents;
    this.subscribedEvents = Object.keys(mappedEvents);
  }

  async init() {
    this.googleTagManager = useScriptGoogleTagManager() as unknown as GoogleTagManager;
  }

  track(eventName: string, eventData?: object) {
    const mapping = this.mappedEvents[eventName];
    if (!mapping) {
      return;
    }

    const data = <Record<string, unknown>>{ ...eventData };
    if (mapping.when && !mapping.when(data)) {
      return;
    }

    if (mapping.nested) {
      // Legacy conversion events keep the { event, value } wrapper their GTM tags expect
      this.googleTagManager?.proxy.dataLayer.push({ event: mapping.name, value: { ...data } });
      return;
    }

    this.googleTagManager?.proxy.dataLayer.push({ event: mapping.name, ...toFlatParams(data) });
  }

  identify() {
    // noop atm
  }

  reset() {
    // noop atm
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  setUserProperties(properties: Record<string, unknown>) {
    // noop atm
  }

  getSubscribedEvents() {
    return this.subscribedEvents;
  }
}
